import type { ReactNode } from 'react';

const BARE_BULLET_MARKER_PATTERN = /^[-*•]$/;
const BULLET_MARKER_PATTERN = /^[-*•]\s+/;
const HEADING_MARKER_PATTERN = /^#{1,6}\s+/;

const BOLD_SEGMENT_PATTERN = /\*\*([^*]+)\*\*/g;

type VacancyDescriptionBlock =
  | {
      content: string;
      id: string;
      type: 'heading' | 'paragraph';
    }
  | {
      id: string;
      items: Array<{
        content: string;
        id: string;
      }>;
      type: 'list';
    };

const isBulletLine = (line: string) => BULLET_MARKER_PATTERN.test(line);

const isBareBulletMarker = (line: string) =>
  BARE_BULLET_MARKER_PATTERN.test(line);

const isMarkedHeading = (line: string) => HEADING_MARKER_PATTERN.test(line);

const stripHeadingMarker = (line: string) =>
  line.replace(HEADING_MARKER_PATTERN, '').trim();

const stripBulletMarker = (line: string) =>
  line.replace(BULLET_MARKER_PATTERN, '').trim();

const isLikelyHeading = (line: string, nextLine: string | undefined) => {
  if (!nextLine || isBulletLine(line)) {
    return false;
  }

  const isShortLine = line.length <= 80;
  const endsWithSentencePunctuation = /[.!?]$/.test(line);

  return (
    isShortLine && (!endsWithSentencePunctuation || isBulletLine(nextLine))
  );
};

const getDescriptionLines = (description: string) => {
  const sourceLines = description
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const lines: string[] = [];

  for (let index = 0; index < sourceLines.length; index += 1) {
    const line = sourceLines[index];
    const nextLine = sourceLines[index + 1];

    if (isBareBulletMarker(line) && nextLine) {
      lines.push(`- ${nextLine}`);
      index += 1;
      continue;
    }

    lines.push(line);
  }

  return lines;
};

const parseVacancyDescription = (description: string) => {
  const lines = getDescriptionLines(description);
  const blocks: VacancyDescriptionBlock[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (isBulletLine(line)) {
      const id = `list-${index}`;
      const items: Array<{ content: string; id: string }> = [];

      while (index < lines.length && isBulletLine(lines[index])) {
        items.push({
          content: stripBulletMarker(lines[index]),
          id: `item-${index}`,
        });
        index += 1;
      }

      blocks.push({ id, items, type: 'list' });
      index -= 1;
      continue;
    }

    if (isMarkedHeading(line) || isLikelyHeading(line, lines[index + 1])) {
      blocks.push({
        content: stripHeadingMarker(line),
        id: `heading-${index}`,
        type: 'heading',
      });
      continue;
    }

    blocks.push({ content: line, id: `paragraph-${index}`, type: 'paragraph' });
  }

  return blocks;
};

const renderFormattedText = (content: string) => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(BOLD_SEGMENT_PATTERN)) {
    const marker = match[0];
    const text = match[1];
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      nodes.push(content.slice(lastIndex, matchIndex));
    }

    nodes.push(
      <strong key={`${matchIndex}-${text}`} className="font-bold">
        {text}
      </strong>
    );
    lastIndex = matchIndex + marker.length;
  }

  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : content;
};

const renderListItemContent = (item: string) => {
  if (item.includes('**')) {
    return renderFormattedText(item);
  }

  const dividerIndex = item.indexOf(':');
  const hasLeadIn = dividerIndex > 0 && dividerIndex <= 64;

  if (!hasLeadIn) {
    return item;
  }

  return (
    <>
      <strong className="font-bold">{item.slice(0, dividerIndex + 1)}</strong>
      {item.slice(dividerIndex + 1)}
    </>
  );
};

interface VacancyDescriptionProps {
  description: string | null;
  noDescriptionLabel: string;
}

export const VacancyDescription = ({
  description,
  noDescriptionLabel,
}: VacancyDescriptionProps) => {
  const blocks = parseVacancyDescription(description ?? noDescriptionLabel);

  return (
    <div className="flex flex-col gap-5 px-4 py-8 text-white md:px-6 md:py-10">
      {blocks.map((block) => {
        if (block.type === 'heading') {
          return (
            <h3
              key={block.id}
              className="font-manrope text-[20px] leading-[1.5] font-semibold tracking-[0.2px] text-white"
            >
              {renderFormattedText(block.content)}
            </h3>
          );
        }

        if (block.type === 'list') {
          return (
            <ul
              key={block.id}
              className="list-disc space-y-0 pl-6 font-manrope text-[16px] leading-[1.5] font-normal tracking-[0.16px] text-white"
            >
              {block.items.map((item) => (
                <li key={item.id}>{renderListItemContent(item.content)}</li>
              ))}
            </ul>
          );
        }

        return (
          <p
            key={block.id}
            className="font-manrope text-[16px] leading-[1.5] font-normal tracking-[0.16px] text-white"
          >
            {renderFormattedText(block.content)}
          </p>
        );
      })}
    </div>
  );
};
